/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';

type StackGap = 'none' | 'sm' | 'md' | 'lg';
type StackPadding = 'none' | 'sm' | 'md' | 'lg';
type StackMaxWidth = 'sm' | 'md' | 'lg' | 'xl';
type StackAlign = 'start' | 'center' | 'end' | 'stretch';
type StackJustify = 'start' | 'center' | 'end' | 'between';
type StackComponent = 'div' | 'section' | 'article' | 'aside' | 'main' | 'header' | 'footer';

interface StackProps {
    children: React.ReactNode;
    /** Gap between children: `none` (0), `sm` (8px), `md` (16px, default), or `lg` (24px). */
    gap?: StackGap;
    /** Inset padding on the stack itself: `none` (0, default), `sm` (16px), `md` (24px), or `lg` (32px). For surfaces that need a card-like inset without being a card (no border/bg). */
    padding?: StackPadding;
    /** Cap on stack width: `sm` (480px), `md` (800px), `lg` (1200px), or `xl` (1400px). Omit for no cap. */
    maxWidth?: StackMaxWidth;
    /** Cross-axis alignment (horizontal). Defaults to `stretch`. */
    align?: StackAlign;
    /** Main-axis alignment (vertical). Defaults to `start`. */
    justify?: StackJustify;
    /** If true, stretches to 100% of parent height. Useful inside flex parents. */
    fullHeight?: boolean;
    /** If true, applies `flex: 1` + `minWidth: 0` so the stack grows to fill a flex-row parent. */
    grow?: boolean;
    /** If true, stretches to 100% of parent width. Pair with `maxWidth` to fill up to a cap. */
    fullWidth?: boolean;
    /** HTML element to render. Defaults to `div`. */
    component?: StackComponent;
    className?: string;
}

const GAP_PX: Record<StackGap, number> = { none: 0, sm: 8, md: 16, lg: 24 };
const PADDING_PX: Record<StackPadding, number> = { none: 0, sm: 16, md: 24, lg: 32 };
const MAX_WIDTH_PX: Record<StackMaxWidth, number> = { sm: 480, md: 800, lg: 1200, xl: 1400 };
const ALIGN_VALUE: Record<StackAlign, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    stretch: 'stretch',
};
const JUSTIFY_VALUE: Record<StackJustify, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    between: 'space-between',
};

type StackStyleProps = {
    $gap: StackGap;
    $padding: StackPadding;
    $maxWidth?: StackMaxWidth;
    $align: StackAlign;
    $justify: StackJustify;
    $fullHeight: boolean;
    $grow: boolean;
    $fullWidth: boolean;
};

const stackStyles = ({
    $gap,
    $padding,
    $maxWidth,
    $align,
    $justify,
    $fullHeight,
    $grow,
    $fullWidth,
}: StackStyleProps) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    gap: GAP_PX[$gap],
    padding: PADDING_PX[$padding] || undefined,
    boxSizing: 'border-box' as const,
    alignItems: ALIGN_VALUE[$align],
    justifyContent: JUSTIFY_VALUE[$justify],
    maxWidth: $maxWidth ? MAX_WIDTH_PX[$maxWidth] : undefined,
    width: $fullWidth ? '100%' : undefined,
    height: $fullHeight ? '100%' : undefined,
    flex: $grow ? 1 : undefined,
    minWidth: $grow ? 0 : undefined,
});

const shouldForwardStackProp = (prop: string) =>
    !['$gap', '$padding', '$maxWidth', '$align', '$justify', '$fullHeight', '$grow', '$fullWidth'].includes(prop);

const DivStack = styled('div', { shouldForwardProp: (p) => shouldForwardStackProp(p as string) })<StackStyleProps>(
    stackStyles,
);
const SectionStack = styled('section', {
    shouldForwardProp: (p) => shouldForwardStackProp(p as string),
})<StackStyleProps>(stackStyles);
const ArticleStack = styled('article', {
    shouldForwardProp: (p) => shouldForwardStackProp(p as string),
})<StackStyleProps>(stackStyles);
const AsideStack = styled('aside', { shouldForwardProp: (p) => shouldForwardStackProp(p as string) })<StackStyleProps>(
    stackStyles,
);
const MainStack = styled('main', { shouldForwardProp: (p) => shouldForwardStackProp(p as string) })<StackStyleProps>(
    stackStyles,
);
const HeaderStack = styled('header', {
    shouldForwardProp: (p) => shouldForwardStackProp(p as string),
})<StackStyleProps>(stackStyles);
const FooterStack = styled('footer', {
    shouldForwardProp: (p) => shouldForwardStackProp(p as string),
})<StackStyleProps>(stackStyles);

const COMPONENTS = {
    div: DivStack,
    section: SectionStack,
    article: ArticleStack,
    aside: AsideStack,
    main: MainStack,
    header: HeaderStack,
    footer: FooterStack,
};

export function Stack({
    children,
    gap = 'md',
    padding = 'none',
    maxWidth,
    align = 'stretch',
    justify = 'start',
    fullHeight = false,
    grow = false,
    fullWidth = false,
    component = 'div',
    className,
}: StackProps) {
    const Component = COMPONENTS[component];
    return (
        <Component
            $gap={gap}
            $padding={padding}
            $maxWidth={maxWidth}
            $align={align}
            $justify={justify}
            $fullHeight={fullHeight}
            $grow={grow}
            $fullWidth={fullWidth}
            className={className}
        >
            {children}
        </Component>
    );
}
/* eslint-enable no-comments/disallowComments */
